import { useEffect } from "react";
import type { Config } from "../../common/interfaces/config";
import { fetchConfig } from "../../common/util/config";
import { AUTH_ROUTE_PATHS } from "../../routing/AuthenticationRoutes";
import { getAffAbo } from "../Authentication/util";
import { store } from "../../store";
import { clearSession } from "../../common/util/cookies";

// Guard against the logout teardown running more than once. The effect fires
// twice under React StrictMode (dev), and the first run's sessionStorage.clear()
// wipes `loginOrigin`, so a second run would read null and fall through to the
// wrong (market-home) branch — redirecting to "/" and racing the real redirect.
let logoutStarted = false;

export default function LogOut() {
  useEffect(() => {
    if (logoutStarted) return;
    logoutStarted = true;

    // Read loginOrigin once, up front, before clearSession() wipes sessionStorage.
    const loginOrigin = sessionStorage.getItem("loginOrigin");
    // Entra logins never write sessionStorage.loginOrigin, so also trust the token-derived flag.
    const isImpersonation = loginOrigin === "impersonation" || store.getState().boot.isImpersonation;

    const clearStorage = async () => {
      clearSession();
    };

    const redirectMarketHomePage = async () => {
      const config = await fetchConfig();
      clearStorage();
      window.location.href = config.secureLinkRedirectPath ? config.secureLinkRedirectPath : window.location.origin;
    };

    // Added this check to make sure if there is gluIdpSessionLogoutUrl not empty in config then session out using the idpSessionLogoutUrl.
    const getLogoutRawLink = (
      config: Pick<Config, "logoutLink" | "useGluuAuth"> & { gluIdpSessionLogoutUrl?: string },
    ) => {
      const { gluIdpSessionLogoutUrl, logoutLink, useGluuAuth } = config;
      if (useGluuAuth && gluIdpSessionLogoutUrl) {
        return gluIdpSessionLogoutUrl;
      }
      return logoutLink;
    };

    const redirectOnLogOut = async () => {
      const { aff, abo } = await getAffAbo();
      const loginFielads = store.getState().loginLocalStorage.loginFields;
      const ssoOrigin = loginFielads.ssoOrigin;
      const locale = loginFielads.locale;
      const config = await fetchConfig();
      const { amwayIdUrlBase, websiteUrlBase } = config;
      const href = getLogoutRawLink(config).replace(
        /{{AMWAYID_BASE_URL}}|{{WEBSITE_URL_BASE}}|{{ISO_COUNTRY_CODE}}|{{AFF}}|{{ABO}}|{{LOCALE}}/g,
        function (matched: string) {
          switch (matched) {
            case "{{AMWAYID_BASE_URL}}":
              return amwayIdUrlBase;
            case "{{WEBSITE_URL_BASE}}":
              return websiteUrlBase;
            case "{{ISO_COUNTRY_CODE}}":
              return ssoOrigin;
            case "{{AFF}}":
              return aff;
            case "{{ABO}}":
              return abo;
            case "{{LOCALE}}":
              return locale;
          }
        },
      );
      await clearStorage();
      window.location.href = href;
    };

    const impersonationLogout = async () => {
      await clearStorage();
      window.location.replace(window.location.origin + AUTH_ROUTE_PATHS.IMPERSONATION);
    };

    if (isImpersonation) {
      impersonationLogout();
    } else if (loginOrigin === "amwayid") {
      redirectOnLogOut();
    } else {
      redirectMarketHomePage();
    }
  }, []);

  return <></>;
}
